import {
    ChangesAlerts,
    ExploreTabType,
    SearchOptions,
    SearchType,
} from '../../types'
import {
    AppState,
    ArchiveStateConstant,
    CachedPreload,
    GlobalVideoCache,
    GridState,
    HealthState,
    InstanceCounter,
    KeyHandlers,
    Lists,
    MailState,
    MenuState,
    MfcRouteInfo,
    ModalStack,
    ModalState,
    Model,
    MYMLState,
    NewsState,
    PMChatState,
    PMState,
    QuickMenuModal,
    RoomHistoryState,
    RoomsState,
    RouteHistory,
    ScreenState,
    SearchResults,
    SearchState,
    SecureState,
    Settings,
    Sidebar,
    SnapVideoState,
    ThrottleTimers,
    TippedModelsState,
    TLayout,
    Transitions,
    UsersState,
    VideoState,
    WhiteboardState,
} from '../../types/state'
import { NOOP } from '../constants'
import {
    DisplayUeopt,
    FloaterPageState,
    LoungeOptions,
    ModalKind,
    MYML_LOCATIONS,
    PMPlaySoundOnUeopt,
    RoomView,
    SettingsType,
    SidebarType,
    VideoType,
    ViewMode,
} from '../constants/enums'
import { FLOATING_ROOM } from '../constants/floaters'
import { BROADCASTING_STATE } from '../constants/myWebcam'
import {
    ModelExplorerConfig,
    ModelExplorerDesc,
    ModelExplorerItem,
    ModelExplorerOrder,
    ModelExplorerSelection,
} from '../mfcLogic/api/modelExplorer'
import { getSessionKey } from '../mfcLogic/history'
import { GridDensityUeopt } from '../mfcLogic/ueopts'
import type { VideoAvatarModel } from '../mfcLogic/videoAvatars'
import {
    getLocalStorageValue,
    localStorageAvailable,
} from '../utilities/storage'
import { parseQuery } from '../utilities/url'

/* Alerts */
export const ALERTSSTATE = {
    alerts: new Map<number, ChangesAlerts>(),
    unread: 0,
}

/* App */
export const defaultAPPSTATE = (): AppState => ({
    alerts: {},
    app: {
        hideFeaturedModels: false,
        authenticated: undefined,
        darkMode: undefined,
        isMobile: undefined,
        isIOS: undefined,
        isIpad: undefined,
        passcode: undefined,
        nsfw: undefined,
    },
    authentication: {},
    myWebcam: {
        broadcasting: BROADCASTING_STATE.UNINITIALIZED,
        isVideoPublisherReady: false,
        isWebcamAvailable: true,
    },
    session: {},
    tipDefaults: {
        anonymousTip: false,
        publicComment: true,
        publicTip: true,
    },
    tokens: {},
})

export const APPSTATE: AppState = {
    ...defaultAPPSTATE(),
}

/* Archives */
export const defaultPrivateState = {
    username: null,
    isEmpty: false,
    dates: null,
    names: null,
    id: null,
}

export const defaultChatState = {
    sessionLabel: null,
    chatType: null,
    messages: [],
    date: null,
    id: null,
    loading: false,
}

export const ARCHIVESTATE: ArchiveStateConstant = {
    state: {
        private: { ...defaultPrivateState },
        showPrivateOnly: false,
        // initialized: false,
        id: null,
        public: {
            username: null,
            isEmpty: false,
            month: null,
            dates: null,
            names: null,
            year: null,
            loaded: {},
            index: {},
        },
        chat: { ...defaultChatState },
    },
}

/* Debug */
export const DEBUGSTATE = {
    debug: {
        log: [],
    },
}

/* Grid */
export const GRIDSTATE: { grid: GridState } = {
    grid: {
        avatarInfoOpenID: null,
        centeredIndex: null,
        scrollOffset: null,
    },
}

/* Health */
export const HEALTHSTATE: { health: HealthState } = {
    health: {
        connecting: true,
        connected: false,
        paused: null,
        status: null,
        error: null,
    },
}
/* Keys */
export const KEY_HANDLERS: KeyHandlers = {
    keydown: [],
    // keyup: [],
    // keypress: [],
}

/* Layout */
export const LAYOUTSTATE: TLayout = {
    layout: {
        expandedNav: undefined,
        expandedSearchMobile: undefined,
        // fullWindow: undefined,
        minimized: undefined, // chat minimized?
        lockedColorScheme: false,
    },
    portal: {
        composeFocused: undefined,
        quietFocus: undefined,
        division: undefined,
        pmListCollapsed: false,
    },
    // portalDimensions: {
    //     height: undefined,
    //     width: undefined,
    //     listeners: 0,
    // },
}

/* Lists */
export const LISTS: Lists = {}

/* Mail */
export const MAILSTATE: MailState = {
    mail: {
        messages: {
            inbox: [],
            sent: [],
        },
        opened: {
            box: null,
            mailId: null,
        },
        loading: true,
        unread: 0,
    },
}

/* Menu */
export const defaultMenuState = {
    persist: {
        mute: true,
    },
    id: null,
}

export const MENUSTATE: { menu: MenuState } = {
    menu: structuredClone(defaultMenuState),
}

/* Modals */
export const MODALSTATE: ModalState = {
    [ModalKind.Dialog]: { type: NOOP, show: false },
    [ModalKind.Drawer]: { type: NOOP, show: false },
    [ModalKind.Confirmation]: { show: false },
    [ModalKind.QuickMenu]: { show: false } as QuickMenuModal,
    [ModalKind.Floater]: {
        /// Multi Modal, keyed by `${id}-${QUICK_REPLY}` or other floater constants
    },
    [ModalKind.FloatingRoom]: {
        id: null,
        type: FLOATING_ROOM,
        show: false,
        expanded: true,
        hideVideo: false,
        showTips: false,
        showLoungePreviews: false,
        minimized: false,
        showZoomVideo: false,
        roomView: RoomView.Room,
        viewMode: ViewMode.Normal,
        restoreViewMode: null,
        reopenViewMode: null,
        split: null,
        landscape: {
            videoColumnWidth: 0,
            chatColumnWidth: 0,
            videoWidth: 0,
            videoHeight: 0,
            avatarHeight: 0,
            heightBelowVideo: { video: 0, avatar: 0 },
            chatSpace: 0,
            drag: {
                originX: 0,
                minVideoWidth: 0,
                maxVideoWidth: 0,
                basisWidth: 0,
                handleTop: 0,
            },
        },
        portrait: {
            width: 0,
            videoHeight: 0,
            zoomedVideoHeight: 0,
            zoomedVideoWidth: 0,
            space: { normal: 0, zoomed: 0, none: 0 },
        },
        theater: {
            videoWidth: 0,
            videoHeight: 0,
            heightBelowVideo: 0,
            chatSpace: 0,
        },
        mobileTheater: { videoWidth: 0, videoHeight: 0, chatSpace: 0 },
        fullWindow: {
            videoWidth: 0,
            videoHeight: 0,
            pillarboxed: false,
            verticalActionBar: false,
            spacer: 0,
            chatSpace: 0,
        },
        basicPm: { space: 0, compactHeader: false },
        avatarUrl: null,
        locations: {
            portrait: null,
            landscape: null,
        },
    },
}
export const MODALSTACK: ModalStack = {
    byPosition: [],
    byKey: {},
}

/* Model Browser */
export interface ModelBrowserFilters {
    category: string
    order: ModelExplorerOrder
    selection: ModelExplorerSelection
    desc: ModelExplorerDesc
    search: string
}
export interface ModelBrowserState {
    config: ModelExplorerConfig | null
    items: Array<ModelExplorerItem>
    total: number
    loading: boolean
    error: string | null
    filters: ModelBrowserFilters
    /** The Video Avatars category's results (VIDEO_AVATARS_CATEGORY) */
    videoAvatars: Array<VideoAvatarModel>
}
export const MODELBROWSERSTATE: { modelBrowser: ModelBrowserState } = {
    modelBrowser: {
        config: null,
        items: [],
        total: 0,
        loading: false,
        error: null,
        filters: {
            category: 'room_topic',
            order: 'cam_score',
            selection: 'online',
            desc: 1,
            search: '',
        },
        videoAvatars: [],
    },
}

/* Video avatars (the Explore shelf and its viewer) */
export interface VideoAvatarFeedState {
    /** Online models with clips, in rail order */
    models: Array<VideoAvatarModel>
    loading: boolean
    /** When `models` last arrived (ms), 0 before the first load */
    fetchedAt: number
    /** The last load failed (the list kept is the one before it) */
    error: boolean
    /** The viewer's model in `models`; `models.length` is the end card */
    viewerIndex: number
    viewerOpen: boolean
}
export const VIDEOAVATARFEEDSTATE: {
    feed: VideoAvatarFeedState
} = {
    feed: {
        models: [],
        loading: false,
        fetchedAt: 0,
        error: false,
        viewerIndex: 0,
        viewerOpen: false,
    },
}

/* MYML */
export const MYMLSTATE: MYMLState = {
    [MYML_LOCATIONS.menu]: {
        results: [],
        id: null,
        timestamp: 0,
    },
    [MYML_LOCATIONS.profile]: {
        results: [],
        id: null,
        timestamp: 0,
    },
    [MYML_LOCATIONS.recommendationPage]: {
        results: [],
        id: null,
        timestamp: 0,
    },
    [MYML_LOCATIONS.room]: {
        results: [],
        id: null,
        timestamp: 0,
    },
}

/* News */
export const NEWSSTATE: NewsState = {
    news: {
        information: {},
        newsItems: [],
        statusMessage: '',
        // the key, not the label — chips and the client both match on keys,
        // and this is MfcNewsFeed's own default view_selection
        viewSelection: 'all_models',
        users: {},
        searchQuery: undefined,
        searchType: undefined,
        isLoading: false,
        isRefreshing: false,
        onlineOnly: false,
        pendingUserIds: [],
        newItemIds: [],
    },
}

/* PMs */
export const getStartOfDayTimestamp = (timestamp) => {
    return new Date(new Date(timestamp).toDateString()).getTime()
}
export const DEFAULT_PMCHATSTATE: PMChatState = {
    isIntentionallyClosed: false,
    isHistoryInitiated: false,
    activeThisSession: false,
    wasPMCommandUsed: false,
    isHistoryUpdated: null,
    listHasToday: false,
    isExhausted: false,
    isLoading: false,
    isTyping: false,
    isOpen: false,
    messages: [],
    history: [],
    latest: [],
    unread: 0,
}
export const PMSTATE: PMState = {
    startOfTodayTimestamp: getStartOfDayTimestamp(Date.now()),
    lastKnownScrollEndIndex: 0,
    lastKnownScrollOffset: 0,
    collapsedList: null,
    isBatchLoading: true,
    unreadFragments: {},
    isSearching: false,
    isExhausted: false,
    skipPubsub: false,
    isLoading: false,
    activeID: null,
    unread: 0,
    chats: {},
}

/* RoomHistory */
export const ROOMHISTORYSTATE: RoomHistoryState = {
    history: [],
}

/* Tipped Models */
export const TIPPEDMODELSSTATE: TippedModelsState = {
    users: [],
}

/* Rooms */
export const ROOMSSTATE: RoomsState = {
    pinnedRooms: [],
    pinHelperID: 0,
    restored: 0,
    rooms: {},
}

/* Router */
export const HISTORY: RouteHistory = {
    lastRoute: [
        { visibleRoute: '', baseRoute: '' },
        { visibleRoute: '', baseRoute: '' },
    ],
    referrer: null,
}
export const TransitionTypes: Transitions = {
    NONE: () => null, // no need for transition (ex: room to room)
    PREVIEW: (id) => `#floating-preview-${id}`, // preview (either room preview or floating preview?)
    SIDEBAR: () => '#sidebar-connections', // an avatar somewher on the page (friends sidebar, rooms or pms listings?)
    ROW: () => '#list-section', // rooms or pms list
    TILE: (id) => `#tile-${id}`, // a grid tile from home, explore, available space, etc
    ROOM: () => '#floating-room', // full room to room preview
    MENU: () => '#menu', //`#menu-${id}`, // model menu
    MODAL: () => '#modal', // any other modal
    FLOATER: (id) => `#floater-${id}`, // floater modal
    NAV: () => '#nav', // nav bar
    PINROOM: (id) => `#pinned-room-${id}`, // pinned rooms
    MINI_PLAYER: (id) => `#floater-${id}`, // mini player, holding the body-level player
}
export const ROUTESTATE: MfcRouteInfo = {
    visibleRoute: undefined,
    visiblePage: undefined,
    floatingPage: undefined,
    floatingRoute: undefined,
    floatingComponent: undefined,
    basePage: undefined,
    baseRoute: undefined,
    baseComponent: undefined,
    visibleQuery: {},
    baseQuery: {},
    hash: undefined,
    slug: undefined,
    slugID: undefined,
    surrogateSlug: undefined,
    surrogateID: undefined,
    preloadCache: new Map<string, CachedPreload>(),
    lastTransitionSource: TransitionTypes.NONE,
}

/* Screen */
const getInitialWindowHeight = () =>
    (typeof window !== 'undefined' && window.innerHeight) || undefined
const getInitialWindowWidth = () =>
    (typeof window !== 'undefined' && window.innerWidth) || undefined

export const SCREENSTATE: { screen: ScreenState } = {
    screen: {
        calculated: false,
        contentHeight: undefined,
        contentWidth: undefined,
        hasPinnedRooms: false,
        orientationType: FloaterPageState.PORTRAIT,
        pinLimit: 1,
        pinLimitReached: false,
        pinsHeight: undefined,
        pinsWidth: undefined,
        portrait: true,
        ratio: 0.5,
        roomHeight: undefined,
        roomWidth: undefined,
        roomX: 0,
        roomY: 0,
        singlePin: false,
        windowHeight: getInitialWindowHeight(),
        windowWidth: getInitialWindowWidth(),
    },
}

/* Search */
const resolveInitialLoadingState = () => {
    if (typeof window !== 'undefined') {
        return document.location.pathname?.startsWith('/search') ? null : false
    }

    return false
}

const initialQueryState = () => {
    if (typeof window === 'undefined') {
        return
    }

    const {
        location: { pathname, search: query },
    } = document

    if (pathname === '/search') {
        const { query: search = '', type = '', view = '' } = parseQuery(query)

        return {
            view,
            search,
            type: type as SearchType,
            fromHomePage: false,
        } as SearchOptions
    }

    return defaultQueryState
}
export const defaultQueryState: SearchOptions = {
    search: '',
    view: '' as ExploreTabType,
    type: '' as SearchType,
    fromHomePage: false,
}

export const defaultResultsState: SearchResults = {
    data: undefined,
    query: undefined,
    timestamp: null,
}

function loadHistory(): Array<string> {
    if (!localStorageAvailable) return []
    try {
        const SEARCH_RESULT_HISTORY = getSessionKey(SEARCH_HISTORY)
        const storageHistory = JSON.parse(
            getLocalStorageValue(SEARCH_RESULT_HISTORY),
        ) as Array<string> | Record<string, string>
        if (Array.isArray(storageHistory)) {
            // filter out single character entries
            const filteredHistory = storageHistory.filter(
                (item) => item.length > 1,
            )
            return filteredHistory
        } else if (typeof storageHistory === 'object') {
            // filter out single character entries
            const filteredHistory = Object.values(storageHistory).filter(
                (item) => item.length > 1,
            )
            return filteredHistory
        } else {
            return []
        }
    } catch (_e) {
        return []
    }
}
export const SEARCH_HISTORY = 'SEARCH_HISTORY_'
export const SEARCHSTATE: SearchState = {
    loading: resolveInitialLoadingState(),
    results: { ...defaultResultsState },
    query: initialQueryState(), //{ ...defaultQueryState },
    history: loadHistory(),
}

/* Secure */
export const SECURESTATE: SecureState = {
    ageVerification: {},
    imageSecurityConfig: {
        snaps: {
            token: undefined,
        },
        img: {
            token_params: undefined,
        },
    },
}

/* Settings */
export const SETTINGSSTATE: Settings = {
    [SettingsType.DEFAULT]: {
        alerts: 0,
        audibleAlertNonModelFriendLogin: 0,
        audibleAlertModelFriendLogin: 0,
        audibleAlertBookmarkedModelFriendLogin: 0,
        audibleAlertModelEntersPrivateOrGroup: 0,
        audibleAlertMyWebcamWatcherJoined: 0,
        animatePreviews: 1,
        animationSpeed: 10,
        colorScheme: null,
        enableAvatarScrollPreview: true,
        enableFloatingPreview: true,
        extraMenuMode: 'popular_rooms',
        display: DisplayUeopt.default,
        displayChosen: false,
        fullWindow: undefined,
        modelListSortRate: 30,
        gridDensity: GridDensityUeopt.medium,
        groupBy: 'state',
        region: 0,
        regions: {
            1: { name: 'Africa', show: true, id: 'AF' },
            2: { name: 'Asia/Pacific Islands', show: true, id: 'AS' },
            4: { name: 'Australia', show: true, id: 'AU' },
            8: { name: 'Central/S. America', show: true, id: 'SA' },
            16: { name: 'Europe/Russia', show: true, id: 'EU' },
            32: { name: 'North America', show: true, id: 'NA' },
            64: { name: 'United Kingdom', show: true, id: 'UK' },
            128: { name: 'Unknown Region', show: true, id: 'UN' },
        },
        show: 'custom',
        sort: 'rating',
        pmNotification: 0,
        // pin to top
        pinFriends: 1,
        pinBookmarks: 1,
        pinTrendingRooms: 0,
        pinPopularRooms: 0,
        pinModelsYouMayLike: 1,
        pinModelsWithNotes: 0,
        pinNewModels: 0,
        pinMissMFC: 0,
        pinPrivateGroupShows: 0,
        pinRecentlyVisitedRooms: 0,
        pinCustomSelection: 1,
        pinPastWinners: 0,
        // pin to top custom amounts
        pinTrendingRoomsAmount: 10,
        pinPopularRoomsAmount: 10,
        pinModelsYouMayLikeAmount: 10,
        pinMissMFCAmount: 100,
        pinRecentlyVisitedRoomsAmount: 10,
        pinCustomSelectionAmount: 10,
        pmNotificationSound: 2,
        pmPlaySoundsOn: PMPlaySoundOnUeopt.receivedMessage,
    },
    [SettingsType.NEWS_NOTIFICATIONS]: {
        news: 3,
    },
    [SettingsType.MY_ACCOUNT]: {
        disableInChatQuickbuy: 0,
        tokenRevealLevel: 2,
        rpRevealLevel: 2,
        truePrivate: 0,
        privateGalleryOptOut: 0,
        administrativeEmails: 1,
        purchaseConfirmationEmails: 1,
    },
    [SettingsType.MY_WEBCAM]: {
        options: 32,
    },
    [SettingsType.ROOM]: {
        allowUserImages: 1,
        availableSpaceView: 'mfcshare',
        chatFontColor: undefined,
        chatFontSize: 'normal',
        hideChat: 0,
        hideVideoModelRoom: 0,
        highlightChatHover: 0,
        highlightFriendsChat: 0,
        highlightLatestChat: 0,
        inlinePM: 32,
        loungeAutoplay: 0,
        loungeOptions: LoungeOptions.off,
        muteBasics: 0,
        muteGuests: 0,
        noEmotePreview: 0,
        showChatHeaderTopic: 1,
    },
    [SettingsType.PRIVACY]: {
        privateMessages: 112,
    },
}

/* Sidebar */
export const SIDEBAR: Sidebar = {
    setting: {
        show: undefined,
        type: SidebarType.Friends,
    },
    state: {
        show: undefined,
        type: SidebarType.Friends,
    },
}

/* Snap Video */
export const SNAPVIDEOSTATE: SnapVideoState = {}

/* Timers */
export const THROTTLE_TIMERS: ThrottleTimers = {
    timers: {
        autoPlay: null,
        health: null,
        internalError: null,
        imageSecurity: null,
        listChanges: null,
        mail: null,
        messages: null,
        myml: null,
        news: null,
        pinnedRoomSession: null,
        reload: null,
        routeCache: null,
        search: null,
        signup: null,
        snaps: null,
        videoAvatarFeed: null,
        videoCleanup: null,
        workerPing: null,
        workerTimeout: null,
    },
    paused: false,
    messages: {
        list: new Set<string>(),
    },
    snaps: {},
}

/* Users */
export const serverSideUsers: { [id: number]: Model } = {
    486121: {
        blurb: 'A place for Premium Members to hang out.',
        id: 486121,
        level: 4,
        name: 'Lounge',
        slug: 'lounge',
    },
    486123: {
        id: 486123,
        slug: 'lounge1000',
        level: 4,
    },
    486124: {
        id: 486124,
        name: 'Lounge10000',
        slug: 'lounge10000',
        level: 4,
    },
    37512: {
        id: 37512,
        name: 'MyFreeCams',
        level: 5,
    },
}
export const serverSideSessionToID: { [session: number]: number } = {}
export const serverSideSlugToID: { [slug: string]: number } = {
    lounge: 486121,
    lounge1000: 486123,
    lounge10000: 486124,
    MyFreeCams: 37512,
}
export const serverSideIdToSlug: { [id: number]: string } = {
    486121: 'lounge',
    486123: 'lounge1000',
    486124: 'lounge10000',
    37512: 'MyFreeCams',
}
export const serverSideIdToName: { [id: number]: string } = {}
/**
 * __Front-end user state__.
 * Source of truth is the worker user state, `state.users`.
 * Updates are applied to `USERSSTATE` as needed via `onChanges`.
 *
 * *** DO NOT ACCESS THIS DIRECTLY ***
 *
 * Use `const user = await loadUserIfNeeded(identifier)` to access this state.
 */
export const USERSSTATE: UsersState = {
    users: serverSideUsers,
    modelsRequested: false,
    modelsLoaded: false,
}

/* Video */

export const VIDEOSTATE: VideoState = {
    video: {
        volume: 0.5,
        mute: false,
    },
    videos: {},
}

export const INSTANCE_COUNTER: InstanceCounter = {}

export const GLOBAL_VIDEO_CACHE: GlobalVideoCache = {
    [VideoType.Room]: { dimensions: { width: 0, height: 0 } },
    [VideoType.StickyPreview]: { dimensions: { width: 0, height: 0 } },
    [VideoType.Menu]: { dimensions: { width: 0, height: 0 } },
}

/* Whiteboard */
export const WHITEBOARDSTATE: WhiteboardState = {
    whiteboard: {
        loading: false,
        open: false,
        identifier: undefined,
    },
}
